/**
 * Which flows a company has — EVT platform administrators, on the company's
 * page. Lists the flows fr-docs offers the company (it can run them, and they
 * are published to the portal); the administrator chooses which of them its
 * people see, or gives it every offered flow.
 *
 * The draft logic is pure (`tenant-flows.ts`). An edit keeps the version it was
 * made on: if another administrator saved in between, the server refuses and
 * the card offers to reload rather than overwrite their choice. A refetch the
 * rest of the page causes (saving settings, inviting someone) never discards
 * unsaved ticks.
 */
import { Badge, Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import type { TenantFlowAccessMode } from '@portal/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getTenantFlows, updateTenantFlows } from '../../api/access'
import { PortalApiError } from '../../api/portal-client'
import { describeApiError, useI18n } from '../../i18n'
import { accessKey } from './AccessPanel'
import {
  canSave,
  canToggle,
  draftChanged,
  editOf,
  type FlowsEdit,
  nextEdit,
  offerUnknown,
  rowsFor,
  saveBody,
  toggled,
  visibleCount
} from './tenant-flows'

export const tenantFlowsKey = (tenantId: string) => ['portal', 'platform', 'flows', tenantId] as const

export function TenantFlowsCard({ tenantId }: { tenantId: string }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const flows = useQuery({ queryKey: tenantFlowsKey(tenantId), queryFn: () => getTenantFlows(tenantId) })
  const view = flows.data
  const [edit, setEdit] = useState<FlowsEdit | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string; conflict?: boolean } | null>(null)

  // A newer answer becomes the base unless there are unsaved ticks (`nextEdit`).
  useEffect(() => {
    if (view) setEdit((prev) => nextEdit(prev, view))
  }, [view])

  // After a conflict the person asked for the current state: their draft goes.
  const reload = async () => {
    setMessage(null)
    const fresh = await flows.refetch()
    if (fresh.data) setEdit(editOf(fresh.data))
  }

  const onSave = async () => {
    if (!edit) return
    setBusy(true)
    setMessage(null)
    try {
      const saved = await updateTenantFlows(tenantId, saveBody(edit))
      setEdit(editOf(saved))
      qc.setQueryData(tenantFlowsKey(tenantId), saved)
      setMessage({ kind: 'success', text: t.access.flowsSaved })
      await Promise.all([
        qc.invalidateQueries({ queryKey: accessKey(tenantId, 'tenant') }),
        qc.invalidateQueries({ queryKey: accessKey(tenantId, 'audit') }),
        qc.invalidateQueries({ queryKey: ['portal', 'platform', 'readiness', tenantId] }),
        qc.invalidateQueries({ queryKey: ['portal', 'platform', 'tenants'] })
      ])
    } catch (err) {
      const conflict = err instanceof PortalApiError && err.issue?.code === 'flow_access_conflict'
      setMessage({ kind: 'danger', text: describeApiError(t, err), conflict })
    } finally {
      setBusy(false)
    }
  }

  const setMode = (mode: TenantFlowAccessMode) => edit && setEdit({ ...edit, draft: { ...edit.draft, mode } })

  // The rows come from the newest answer (what fr-docs offers now); the ticks from the edit.
  const shown = view ?? edit?.base
  const draft = edit?.draft
  const unknown = shown ? offerUnknown(shown) : false
  const count = shown && draft ? visibleCount(shown, draft) : null

  return (
    <section className="card">
      <div className="card-head">
        <div className="card-title">{t.access.flowsTitle}</div>
      </div>
      <div className="card-pad access-invite">
        <p className="muted access-help">{t.access.flowsHelp}</p>
        {flows.isLoading || (shown && !edit) ? (
          <Spinner size={14} />
        ) : flows.error && !shown ? (
          <Notice kind="danger">{describeApiError(t, flows.error)}</Notice>
        ) : shown && edit && draft ? (
          <>
            {edit.base.version === 0 && <Notice kind="warning">{t.access.flowsNotChosen}</Notice>}
            <div className="access-flow-modes" role="radiogroup" aria-label={t.access.flowsTitle}>
              <label className="access-flow-mode">
                <input
                  type="radio"
                  name={`flow-mode-${tenantId}`}
                  checked={draft.mode === 'selected'}
                  onChange={() => setMode('selected')}
                />
                <span>
                  <span>{t.access.flowsModeSelected}</span>
                  <span className="muted access-sub">{t.access.flowsModeSelectedHelp}</span>
                </span>
              </label>
              <label className="access-flow-mode">
                <input
                  type="radio"
                  name={`flow-mode-${tenantId}`}
                  checked={draft.mode === 'all'}
                  onChange={() => setMode('all')}
                />
                <span>
                  <span>{t.access.flowsModeAll}</span>
                  <span className="muted access-sub">{t.access.flowsModeAllHelp}</span>
                </span>
              </label>
            </div>

            {unknown && <Notice kind="warning">{t.access.flowsOfferedError(shown.offeredError ?? '')}</Notice>}
            {!unknown && !shown.flows.some((f) => f.offered) && (
              <Notice kind="info">{t.access.flowsNoneOffered}</Notice>
            )}

            <ul className="access-flow-list">
              {rowsFor(shown, draft).map((f) => {
                const on = draft.mode === 'all' ? f.offered : draft.chosen.has(f.flowId)
                // Not offered is a fact only when fr-docs answered; otherwise it is unknown, and said so once above.
                const gone = !f.offered && !unknown
                return (
                  <li key={f.flowId} className={gone ? 'is-gone' : undefined}>
                    <label className="access-flow">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={!canToggle(f, draft)}
                        onChange={() => setEdit({ ...edit, draft: toggled(draft, f.flowId) })}
                      />
                      <span className="access-flow-text">
                        <span className="access-person">
                          <span>{f.displayName}</span> <span className="muted access-sub mono">{f.flowId}</span>
                          {gone && <Badge kind="warning">{t.flows.unavailable}</Badge>}
                        </span>
                        {f.description && <span className="muted access-sub">{f.description}</span>}
                        {gone && <span className="access-warn">{t.access.flowsNotOffered}</span>}
                        {f.offered && !f.published && (
                          <span className="muted access-sub">{t.access.flowsNotPublished}</span>
                        )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>

            {count !== null && (
              <p className="muted access-help">
                {draft.mode === 'all' ? t.access.flowsSummaryAll(count) : t.access.flowsSummary(count)}
              </p>
            )}
            <div className="access-settings-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy || !canSave(edit)}
                onClick={() => void onSave()}
              >
                {busy ? <Spinner size={13} /> : <Icon name="check" size={13} />} {t.access.flowsSave}
              </button>
              {draftChanged(edit.base, draft) && !busy && (
                <span className="muted access-sub">{t.access.flowsUnsaved}</span>
              )}
            </div>
            {message && (
              <Notice kind={message.kind}>
                {message.text}{' '}
                {message.conflict && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void reload()}>
                    <Icon name="refresh" size={12} /> {t.access.flowsReload}
                  </button>
                )}
              </Notice>
            )}
          </>
        ) : null}
      </div>
    </section>
  )
}
